import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontSizes, FontWeights, mergeStyleSets, mergeStyles, IconButton } from '@fluentui/react';

import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { CommandIcon } from 'src/components/base/CommandIcon';

import { insensStringsComparator, areStringsInsensitivelyEqual } from 'shared/utils/utils';
import { runShortAction } from 'src/services/utils';

import { officeStore, salesforceStore } from 'src/stores';

import { SubstitutionPicker, classNames as substitutionClassNames } from 'src/components/base/fields/SubstitutionPicker';
import { useSubstitutionPicker, useConversion, getSubstitutionParser } from 'src/components/base/fields/utils';
import * as QA from 'shared/utils/queryAnalysis';


const textStyle = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const classNames = mergeStyleSets({
  field: {
    display: 'flex',
    flexDirection: 'row',
    padding: '8px',
    cursor: 'default',
    selectors: {
      ':hover': {
        backgroundColor: '#eaeaea',
      },
    },
  },
  fieldInfo: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
    marginRight: '4px',
    selectors: {
      '& > .name' : mergeStyles(textStyle, {
        fontSize: FontSizes.medium,
        fontWeight: FontWeights.regular,
        color: '#333',
      }),
      '& > .description' : mergeStyles(textStyle, {
        fontSize: FontSizes.small,
        color: '#666',
      })
    },
  },
  selectedField: {
    border: '1px solid black',
  },
});


const Ftn = QA.FieldTypeName;
export const SupportedTypes = [Ftn.Id, Ftn.Reference];

export function getDefaultValue(rawField) {
  return '';
}

const FilterCondition = { 
  Like: 'like', 
  Equal: 'equal'
};

const SearchBy = {
  Id: 'by-id',
  Field: 'by-field',
  NameField: 'by-name-field'
};

export const ValueKey = {
  Id: 'id',
  Field: 'field',
} 

const LookupData = {
  RequiredFields: [QA.KnownFields.Id, QA.KnownFields.Name],
  Limit: {
    Search: 20,
    Id: 1,
  }
};


/// TODO: remove
const item = {
  ranges: [], 
  isSingleCell: false, 
  list: [],
  selectedKey: undefined, 
  // value: isValidSalesforceId ? 'cellValue' : ''
};

const listItem = {
  id: 'salesforce id', // TODO: add
  key: '',
  value: 'equal to id',
  object: {}, // From cache
  description: '',
  text: '',
}

function useSubs(text, allowSubstitutions) {
  if (allowSubstitutions) {
    const { hasSubstitutions } = tryParseSubstitution(text);
    const value = !hasSubstitutions ? text : '';
    return { substitution: hasSubstitutions ? text : undefined, value };
  }
  
  return { value: text };
}

/// TODO: Remove




export function LookupField({ 
  rawField, objectName, fieldName, value: initialValue, error, onChange, disabled, substitutionProps
}) {
  const [items, setItems] = useState([]);
  const columnData = { rawField, objectName, fieldName };
  
  const parseSubstitution = useMemo(() => {
    const getDefault = () => getDefaultValue(rawField);
    return getSubstitutionParser(substitutionProps, getDefault);
  }, [rawField, substitutionProps]);
 
  const customLookup = !!fieldName;

  const { value = initialValue, substitution } = parseSubstitution(initialValue);

  const [selectedKey, setSelectedKey] = useState(value);

  const sProps = useSubstitutionPicker({ substitutionProps, disabled, onChange });

  useEffect(() => {
    setItems([]);
    loadOnMount();
  }, [rawField]);

  async function loadOnMount() {
    if (substitution) {
      return;
    }
    const list = await fetchAndUpdate({
      items: [{ pattern: value, columnData }], 
      searchBy: customLookup ? SearchBy.NameField : SearchBy.Id,
      limitBy: LookupData.Limit.Id
    });
    const selectedItem = list.find(x => x.value[customLookup ? ValueKey.Field : ValueKey.Id] === value);
    const error = !selectedItem
      ? value ? `Unable to find an object with such ${customLookup ? fieldName : 'id'} ` : 'Select a valid reference / id'
      : '';
    error && onChange({value, error });
  }

  function handleChange(item) {
    let newValue = item.value[ValueKey.Id];
    setSelectedKey(newValue)
    if (customLookup) {
      newValue = item.value;
    }
    onChange({ value: newValue });
  }

  async function fetchAndUpdate(params) {
    const response = await fetchObjects(params);
    const { list, selectedKey: updatedValue } = response[0];
    const i = objectsToList(list, false);
    setItems(i);
  
    if (updatedValue) {
      if (customLookup) {
        setSelectedKey(updatedValue);
      }
      else if (value !== updatedValue) {
        onChange({ value });
      }
    }
    
    return i;
  }

  async function handleSearch(filter) {
    await fetchAndUpdate({ 
      items: [{ pattern: filter, columnData }], searchBy: SearchBy.Field, limitBy: LookupData.Limit.Search 
    });
  }


  return (
    <div className={substitutionClassNames.withTargetPicker}>
      <LookupFieldComponent
        substitution={substitution}
        items={items}
        error={error}
        onFetchObjects={handleSearch}
        selectedKey={selectedKey}
        disabled={disabled}
        onChange={handleChange}
      />
      { <SubstitutionPicker {...sProps} /> }
    </div>
  );
}


export function LookupFieldComponent({ 
  error, selectedKey, substitution, items: list = [], onFetchObjects, disabled, onChange: onChanged 
}) {
  const handleSearch = async (filter = '') => {
    await onFetchObjects(filter)
  }

  const handleSelectionChanged = (event, selectedItem) => {
    onChanged(selectedItem);
  };

  const handleFilterChanged = (filter) => {
    if (filter === '') {
      handleSearch(filter); // Show cached items when filter string is empty (but not null!)
    }
  };

  const handleOpenInSalesforce = (event, item) => {
    event.stopPropagation();
    officeStore.openInSalesforceById(item.object[QA.KnownFields.Id]);
  }

  const handleRenderCell = (item, isSelected, onClick) => (
    <div
      className={`${classNames.field} ${isSelected && classNames.selectedField}`}
      onClick={(event) => onClick(event, item)}
      data-is-focusable="true">
      <div className={classNames.fieldInfo}>
        <span className="name">{item.object?.[item.nameFieldName] ?? item.text}</span>
        { item.object && <span className="description">{item.description}</span> }
      </div>
      { item.object && <CommandIcon
        iconName={'xappex-share'}
        tooltip={'Open this object in Salesforce'} 
        onClick={(e) => handleOpenInSalesforce(e, item)}/> }
    </div>
  )

  return (
    <SearchableComboBox 
      placeholder={'Not selected'} 
      searchHintText={`'Enter' to \uD83D\uDD0E`}
      options={list} 
      selectedKey={selectedKey} 
      text={substitution}
      onRenderCell={handleRenderCell}
      onFilterChange={handleFilterChanged}
      onSearch={handleSearch}
      onChange={handleSelectionChanged} 
      disabled={disabled}
      errorMessage={error}
    />
  );
};



function objectsToList(objects, includeNotSelected = true) {
  return [
    ...(includeNotSelected ? [{ text: 'Not selected' }] : []),
    ...objects.map(({ objectName, object, nameFieldName }, index) => {
      const id = object[QA.KnownFields.Id];
      const name = object[nameFieldName];
      const description = `${objectName}: ${id}`
      return { key: id, text: `${name} (${objectName})`, value: { [ValueKey.Id] : id, [ValueKey.Field] : name }, description, object };
    })
  ];
}


async function fetchObjects({ items, searchBy, limitBy }, objectsStore = salesforceStore) {
  return runShortAction(async () => {
    const { objectsCache } = objectsStore;  

    const requests = items.map(x => {
      const { columnData, pattern } = x;
      return requestObjectData({ objectsCache, pattern, columnData, searchBy, limitBy });
    });  
    
    const queries = requests.flatMap(x => x.queries.map(y => y.query));
    const runQueriesResponse = queries.length ? await objectsStore.runQueries(queries) : {};
    // TODO: check response.error

    let runQueriesResponseOffset = 0;

    return requests.map(requestData => {
      const { queries, pattern } = requestData;
      
      const { results = [] } = runQueriesResponse;
      const count = queries.length;
      const responses = results.slice(runQueriesResponseOffset, runQueriesResponseOffset + count);
      runQueriesResponseOffset += count;

      const { objects: responseObjects, selectedId } = responsesToObjects({ objectsCache, requestData, responses });
       
      let selectedKey = null;
      if (pattern) {
        if (searchBy === SearchBy.Id) {
          // Pattern might be in old format, but for IDs we always use new 18-digit format
          selectedKey = objectsCache.getIdReplacement(pattern);
        }
        else if (searchBy === SearchBy.NameField && selectedId) {
          selectedKey = selectedId;
        }
      }
      return { list: responseObjects, selectedKey };
    });
  });
}

function requestObjectData({ objectsCache, pattern, columnData, searchBy = SearchBy.Field, limitBy = Number.POSITIVE_INFINITY }) {
  const { rawField, objectName, fieldName } = columnData;
  const type = QA.getLookupType(rawField);
  const referencesTo = QA.getRelationshipReferences(rawField);
  
  let cachedByNameFieldId;
  const getNameField = objectName => fieldName || QA.getNameFieldName(objectName);

  const getFromCache = (objectName) =>
    objectsCache.getByObjectName(objectName)
      .map(object => ({ objectName, object, nameFieldName: getNameField(objectName) }))
      .filter(({ object, nameFieldName }) => {
        if (object.hasOwnProperty(nameFieldName)) {
          if (searchBy === SearchBy.NameField && pattern) {
            if (object[nameFieldName] === pattern) {
              cachedByNameFieldId = object[QA.KnownFields.Id];
            }
          }
          return true;
        };
        return false;
      });
  
  const cachedObjects = pattern && searchBy === SearchBy.Field
    ? []
    : type === QA.LookupType.Id ? getFromCache(objectName) : referencesTo.flatMap(x => getFromCache(x));

  const request = { type, searchBy, pattern, cachedObjects, cachedByNameFieldId, queries: [] };

  let isCached = !pattern || cachedByNameFieldId;
  if (!isCached && searchBy === SearchBy.Id) {
    const cached = objectsCache.getById(pattern);
    // We might try to find object by id in 'cachedObjects', but it's faster via objectsCache
    if (cached && cached.object[getNameField(cached.objectName)]) {
      isCached = true;
    }
  }

  if (isCached) {
    return request;
  }

  const pushQuery = (objectName) => {
    const nameFieldName = fieldName || QA.getNameFieldName(objectName);
    const fieldsToReturn = [QA.KnownFields.Id, nameFieldName];
    const query = getQuery({ 
      objectName, fieldsToReturn, limitBy, filter: {
        fields: [SearchBy.Id === searchBy ? QA.KnownFields.Id : nameFieldName],
        condition: [SearchBy.Id, SearchBy.NameField].includes(searchBy)  ? FilterCondition.Equal : FilterCondition.Like,
        pattern, 
      }
    }) ;
    request.queries.push({ objectName, fieldsToReturn, nameFieldName, query });
  }

  type === QA.LookupType.Id
    ? pushQuery(objectName)
    : referencesTo.forEach(objectName => pushQuery(objectName));

  return request;
}

function getQuery({ fieldsToReturn, objectName, filter = {}, limitBy = Number.POSITIVE_INFINITY }) {
  const { condition = FilterCondition.Like, fields: fieldsToFilterBy = [], pattern = '' } = filter;
  
  const toCondition = (field) => condition === FilterCondition.Like
  ? `${field} LIKE '%${pattern}%'`
  : `${field} = '${pattern}'`;
  
  const select = `SELECT ${fieldsToReturn.join(', ')} FROM ${objectName}`;
  const where = pattern ? `WHERE ${fieldsToFilterBy.map(x => toCondition(x)).join(' OR ')}` : '';
  const limit = limitBy === Number.POSITIVE_INFINITY ? '' : `LIMIT ${limitBy}`;

  return `${select} ${where} ${limit}`;
}


function responsesToObjects({ objectsCache, requestData, responses }) {
  const { queries, cachedObjects, cachedByNameFieldId, pattern, searchBy } = requestData;

  let selectedId = cachedByNameFieldId;

  const objects = [
    ...cachedObjects,
    ...responses.flatMap((response, index) => {
      // Error might happen because we request 'Name' fields, but not all objects have it (see Task for instance)
      // Maybe we should provide a possibility for an user to choose fields to query/filter by
      const { result, error } = response; 
      const { objectName, fieldsToReturn, nameFieldName } = queries[index];

      return result
        ? arrayToObjects(result, fieldsToReturn).map(object => {
          const id = object[QA.KnownFields.Id]; // Here we always get ID in new 18-digits format
          objectsCache.add(objectName, id, object);
          // We replace 15-digit IDs from reports with 18-digit IDs,
          // therefore we need to add them into the cache too
          if (searchBy === SearchBy.Id) {
            objectsCache.addIdReplacement(pattern, id);
          }
          if (searchBy === SearchBy.NameField && areStringsInsensitivelyEqual(pattern, object[nameFieldName])) {
            selectedId = id;
          }
          
          return { objectName, object, nameFieldName };
        })
        : [];
    })
  ].sort((a, b) => insensStringsComparator(a.object[a.nameFieldName], b.object[a.nameFieldName]));

  return { objects, selectedId };
}

function arrayToObjects(result, fieldsToReturn) {
  return result.map(row => row.reduce((out, v, i) => {
    out[fieldsToReturn[i]] = v;
    return out;
  }, {}));
}


export default LookupField;